import * as React from 'react';

import { useMutation, useQuery } from '@apollo/client';

import {
  AddToClickAndCollectMutation,
  AddToClickAndCollectMutationVariables,
  GetCatalogProductViewQuery,
  GetStoresQuery,
  GetStoresQueryVariables,
} from '@notino/shared/definitions/types';

import { useSettings } from '@containers/ProductDetailContainer/hooks/useSettings';

import addToClickAndCollectMutation from './mutations/addToClickAndCollect.graphql';
import getStoresQuery from './queries/stores.graphql';
import { redirectToClickAndCollectCart } from './utils';

export const useClickAndCollect = (
  variant: NonNullable<
    GetCatalogProductViewQuery['productDetailByCatalogMasterId']
  >['variants'][number]
) => {
  const { Settings: { isNewShoppingCartEndpointEnabled: isNewEndpoint } = {} } =
    useSettings();

  const { data: stores, loading: storesLoading } = useQuery<
    GetStoresQuery,
    GetStoresQueryVariables
  >(getStoresQuery, {
    variables: { catalogId: Number(variant.catalogId) },
  });

  const [addToClickAndCollectCart, addToClickAndCollectData] = useMutation<
    AddToClickAndCollectMutation,
    AddToClickAndCollectMutationVariables
  >(addToClickAndCollectMutation, {
    onCompleted: (response) => {
      if (response.addProductToClickAndCollect?.cartId) {
        redirectToClickAndCollectCart(
          response.addProductToClickAndCollect.cartId,
          isNewEndpoint ?? false
        );
      }
    },
  });

  const handleAddToClickAndCollect = React.useCallback(
    async (warehouseCode: string) => {
      await addToClickAndCollectCart({
        variables: {
          productId: Number(variant.webId),
          warehouseCode: warehouseCode,
        },
      });
    },
    [addToClickAndCollectCart, variant.webId]
  );

  return React.useMemo(
    () => ({
      stores,
      storesLoading,
      handleAddToClickAndCollect,
      addToClickAndCollectData,
    }),
    [
      stores,
      storesLoading,
      handleAddToClickAndCollect,
      addToClickAndCollectData,
    ]
  );
};
